import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { toast } from 'react-toastify';
import { Avatar, Menu, MenuItem } from '@mui/material';
import { MoreVert } from '@mui/icons-material';

// core components
import AppTable from 'components/new_components/app-table';
import UserTableActions from './UserTableActions';
import UserModal from './UserModal';
import UserDeleteModal from './UserDeleteModal';

// images
import avatar from 'assets/img/avatar.jpg';

// others
import { initialUserValues } from './utils/constants';

// redux
import { connect } from 'react-redux';
import { GetAllMerchantUsers } from 'store/actions/merchantActions';
import PageHeader from 'components/new_components/PageHeader';
// import AuthNotificationBar from "../AccountSetup/TwoFactorAuth/AuthNotificationBar";
// import { GetRemaining } from '../../../components/Pricing/usePricingHook';

//Userguide
import AppUserGuide from 'components/new_components/AppUserGuide';
import { steps } from './steps';
import { UserGuideContext } from 'store/context/UserGuideContextProvider';
import AppArticle from 'components/new_components/AppArticle';

//translation
import { useTranslation } from 'react-i18next';
import { Plus, Search } from 'lucide-react';
import { capitalizeFirstLetter } from 'utils';
// import { capitalizeFirstLetter } from 'utils';

const NO_PER_PAGE = 8;

const Users = (props) => {
    const { all_users, GetAllMerchantUsers, pricing_plan, userguide_info, all_article_header, role } = props;
    // state
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState();
    const [modalPayload, setModalPayload] = useState(initialUserValues);
    const [loading, setLoading] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deletePayload, setDeletePayload] = useState({});
    const [page, setPage] = useState(1);
    // const [filteredUsers, setFilteredUsers] = useState(all_users);
    const [allowedUser, setAllowedUser] = useState(0);

    //Userguide
    const [openUserGuide, setOpenUserGuide] = useState(false);
    const [openArticle, setOpenArticle] = useState(false);

    const [filterValue, setFilterValue] = useState('');

    const [roleFilter, setRoleFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');

    //userguide
    const stepsTargetRef = useRef([]);
    const { updateOneUserGuide, toggleUserGuide, setToggleUserGuide } = useContext(UserGuideContext);

    //transaltion
    const { t } = useTranslation('user');

    //useMemo
    const oneArticle = useMemo(
        () => all_article_header?.find((article) => article?.name === 'Users'),
        [all_article_header]
    );

    // functions
    // logic
    const openModal = (mode, payload = initialUserValues) => {
        setModalOpen(true);
        setModalMode(mode);
        setModalPayload(payload);
    };
    const closeModal = () => {
        setModalOpen(false);
        setTimeout(() => setModalPayload({}), 1000);
    };
    const openDeleteModal = (payload = initialUserValues) => {
        setDeleteModalOpen(true);
        setDeletePayload(payload);
    };
    const closeDeleteModal = () => {
        setDeleteModalOpen(false);
        setTimeout(() => setDeletePayload({}), 1000);
    };
    const handlePageChange = (page) => {
        setPage(page);
    };
    // const handleSearch = (e) => {
    //     const value = e.target.value;
    //     const filteredUsers = all_users?.filter((user) => user?.user_detail?.email?.includes(value));
    //     setFilteredUsers(filteredUsers);
    // };
    // async functions
    const getAllMerchantUsers = async () => {
        setLoading(true);
        const res = await GetAllMerchantUsers();
        setLoading(false);
        if (!res.message) {
            toast.error(res.message);
        }
    };

    //UserGuide
    const OpenUserGuide = () => {
        setOpenUserGuide(true);
    };
    const CloseUserGuide = () => {
        updateOneUserGuide({ users: true });
        setOpenUserGuide(false);
    };
    const OpenArticle = () => {
        setOpenArticle(true);
    };
    const CloseArticle = () => {
        setToggleUserGuide({ ...toggleUserGuide, users: false });
        setOpenArticle(false);
    };

    // useEffect
    useEffect(() => {
        getAllMerchantUsers();
    }, []);

    useEffect(() => {
        !userguide_info ? OpenUserGuide() : toggleUserGuide.users ? OpenArticle() : null;
    }, [userguide_info, toggleUserGuide.users]);
    // constants
    const columns = [
        {
            title: t('table.columns.column2'),
            render: (row) => (
                <>
                    {row?.user_detail?.first_name || row?.user_detail?.last_name ? (
                        <span className="flex items-center justify-start gap-1.5">
                            <Avatar
                                alt={`${row?.user_detail?.first_name} ${row?.user_detail?.last_name}`}
                                src={row?.user_detail?.picture || avatar}
                                sx={{ mx: '0', width: '20px', height: '20px' }}
                            />
                            <span>{row?.user_detail?.first_name + ' ' + row?.user_detail?.last_name}</span>
                        </span>
                    ) : (
                        <span>--</span>
                    )}
                </>
            ),
        },
        {
            title: t('table.columns.column3'),
            key: 'email',
            render: (row) => <>{row?.user_detail?.email}</>,
        },
        {
            title: t('table.columns.column4'),
            key: 'role',
            render: (row) => <span className="capitalize">{row?.role}</span>,
        },
        {
            title: t('table.columns.column5'),
            key: 'status',
            render: (row) => {
                const isActive = Boolean(row?.user_detail?.first_name || row?.user_detail?.last_name);
                return (
                    <div className="w-[fit-content] px-2 py-1 border border-[#F1F5F9] bg-transparent rounded-sm flex gap-1 items-center justify-center">
                        <div
                            className={`${
                                isActive ? 'bg-[#0B885A]' : 'bg-[#94A3B8]'
                            } flex-shrink-0 w-[10px] h-[10px] rounded-full`}
                        />

                        <span className="text-[#64748B] font-medium">
                            {isActive ? t('table.columns.active') : t('table.columns.inactive')}
                        </span>
                    </div>
                );
            },
        },
        {
            title: <MoreVert className="rotate-90" sx={{ color: '#64748B' }} />,
            align: 'center',
            key: role !== 'auditor' && 'action',
            render: (row) =>
                role !== 'auditor' && (
                    <UserTableActions row={row} openModal={openModal} openDeleteModal={openDeleteModal} />
                ),
        },
    ];

    // set number of users allowed based on plans
    useEffect(() => {
        setAllowedUser(pricing_plan?.users);
    }, [pricing_plan]);

    // const { reminderCount, showReminder } = GetRemaining(
    //     pricing_plan?.users,
    //     all_users?.filter((user) => user?.user_detail?.user_type !== 'support')?.length
    // );

    useEffect(() => {
        !userguide_info ? OpenUserGuide() : toggleUserGuide.users ? OpenArticle() : null;
    }, [userguide_info, toggleUserGuide.users]);

    const pageData = useMemo(() => {
        const start = (page - 1) * NO_PER_PAGE;
        const stop = start + NO_PER_PAGE;

        let filteredUsers = all_users
            ? all_users?.filter((user) => {
                  let notSupport = true;
                  notSupport = user?.user_detail?.user_type !== 'support';

                  let statusCondition = true;
                  if (statusFilter && statusFilter !== 'all') {
                      const isActive = Boolean(user?.user_detail?.first_name || user?.user_detail?.last_name);
                      statusCondition = isActive === (statusFilter === 'active' ? true : false);
                  }

                  let roleCondition = true;
                  if (roleFilter && roleFilter !== 'all') {
                      roleCondition = user?.role?.toLowerCase() === roleFilter?.toLowerCase();
                  }

                  let filterCondition = true;
                  if (filterValue) {
                      filterCondition =
                          (user?.user_detail?.first_name + ' ' + user?.user_detail?.last_name)
                              ?.toLowerCase()
                              ?.includes(filterValue?.toLowerCase()) ||
                          user?.user_detail?.email?.toLowerCase()?.includes(filterValue?.toLowerCase());
                  }

                  return notSupport && statusCondition && roleCondition && filterCondition;
              })
            : [];

        return filteredUsers ? filteredUsers?.sort((a, b) => b?.id - a?.id)?.slice(start, stop) : [];
    }, [page, all_users, statusFilter, roleFilter, filterValue]);

    const roles = useMemo(() => {
        const data = new Set([]);
        all_users?.forEach((datum) => data.add(datum.role));
        return Array.from(data);
    }, [all_users]);

    return (
        <>
            <PageHeader browserTitle="Users | Smartcomply" />
            <div className="bg-white rounded-sm px-3 pt-3 mx-4 my-6">
                {/* {showReminder && <SubscriptionAlert isDashboardPage={false} limitedNumber={`${reminderCount} users`} />} */}
                <AppTable
                    columns={columns}
                    data={pageData}
                    sorter={(a, b) => b?.id - a?.id}
                    title={
                        <TopContent
                            filterValue={filterValue}
                            setFilterValue={setFilterValue}
                            roleFilter={roleFilter}
                            setRoleFilter={setRoleFilter}
                            setStatusFilter={setStatusFilter}
                            statusFilter={statusFilter}
                            roles={roles}
                        />
                    }
                    dataLength={all_users?.filter((user) => user?.user_detail?.user_type !== 'support')?.length}
                    noPerPage={NO_PER_PAGE}
                    page={page}
                    onPageChange={handlePageChange}
                    loading={loading}
                    actions={[
                        <button
                            className="rounded-md text-white bg-[#202D66] font-semibold flex items-center px-3 text-sm gap-1 h-9 disabled:opacity-80"
                            onClick={() => openModal('add')}
                            ref={(el) => (stepsTargetRef.current[0] = el)}
                            key={13}
                            disabled={
                                allowedUser <=
                                    all_users?.filter(
                                        (user) =>
                                            user?.user_detail?.user_type !== 'support' &&
                                            user?.user_detail?.user_type !== 'auditor'
                                    )?.length || role === 'auditor'
                            }
                        >
                            <Plus className="text-white" />
                            <span>{t('createUser')}</span>
                        </button>,
                    ]}
                    sx={{
                        tableHeader: {
                            background: '#F1F5F9 !important',
                            py: 0,
                        },
                        tableHeadCell: {
                            py: 1.5,
                            color: '#64748b !important',
                        },
                        tableCell: {
                            py: 1.2,
                            color: '#64748b !important',
                            fontSize: '13.5px',
                            borderBottom: '1px solid #E2E8F0',
                        },
                        tableTopbar: {
                            px: 0,
                            pb: 1.5,
                            '& .MuiCardHeader-action': {
                                mx: 0,
                            },
                        },
                        tableTopWrapper: {
                            title: {
                                mt: 0,
                            },
                            card: '[&_span]:mb-0',
                        },
                    }}
                    border
                    borderStyles={{
                        border: 'none',
                        borderRadius: 6,
                    }}
                    // search={true}
                    // onSearch={handleSearch}
                />
            </div>

            <UserModal
                open={modalOpen}
                handleClose={closeModal}
                payload={modalPayload}
                isAddModal={modalMode === 'add'}
            />
            <UserDeleteModal open={deleteModalOpen} handleClose={closeDeleteModal} payload={deletePayload} />

            <AppUserGuide
                open={openUserGuide}
                onClose={CloseUserGuide}
                steps={steps}
                targets={stepsTargetRef.current}
            />
            <AppArticle
                open={openArticle}
                handleClose={CloseArticle}
                title="Users"
                headerId={oneArticle?.id}
                icon={true}
            />
        </>
    );
};

const TopContent = ({ ...props }) => {
    const { filterValue, setFilterValue, statusFilter, setStatusFilter, roleFilter, setRoleFilter, roles } = props;
    const [statusAnchor, setStatusAnchor] = useState(null);
    const [roleAnchor, setRoleAnchor] = useState(null);

    return (
        <div className="flex items-start justify-between gap-4">
            <div className="flex w-[50%] items-end gap-3">
                <div className="relative">
                    <input
                        type="search"
                        isClearable
                        className="w-full min-w-[210px] pl-8 font-normal placeholder:text-[#94A3B8] text-[#64748B] text-sm focus:outline-none border-none outline-none rounded p-[10px] h-9 data-[hover=true]:!border-[#395BA9] data-[focus=false]:!border-[#395BA9] min-h-9 border !border-[hsl(214,32%,91%)] bg-transparent focus:outline-none outline-none"
                        placeholder="Search"
                        value={filterValue}
                        onChange={(e) => setFilterValue(e.target.value)}
                    />
                    <div className="absolute top-0 left-2 h-full flex items-center justify-center">
                        <Search className="w-[15px] h-[15px] text-[#94A3B8]" />
                    </div>
                </div>

                <div className="flex gap-3">
                    <button
                        // endContent={
                        //     <Image src={ArrowDownIcon} alt="ArrowDownIcon" className="object-contain" />
                        // }
                        onClick={(e) => setStatusAnchor(e.target)}
                        className="h-9 gap-[6px] rounded border !border-[hsl(214,32%,91%)] px-2 py-[6px] text-sm font-medium text-[#64748B]  w-[max-content]"
                    >
                        Status: {capitalizeFirstLetter(statusFilter)}
                    </button>

                    <Menu
                        className="shadow-[none]"
                        open={Boolean(statusAnchor)}
                        anchorEl={statusAnchor}
                        onClose={() => setStatusAnchor(null)}
                    >
                        <MenuItem
                            className="!text-sm text-[#64748b] border-b border-[#F1F5F9]"
                            onClick={() => setStatusFilter('all')}
                        >
                            All
                        </MenuItem>
                        <MenuItem
                            className="!text-sm text-[#64748b] border-b border-[#F1F5F9]"
                            onClick={() => setStatusFilter('active')}
                        >
                            Active
                        </MenuItem>
                        <MenuItem className="!text-sm" onClick={() => setStatusFilter('inactive')}>
                            Inactive
                        </MenuItem>
                    </Menu>
                </div>
                <div className="flex gap-3">
                    <button
                        // endContent={
                        //     <Image src={ArrowDownIcon} alt="ArrowDownIcon" className="object-contain" />
                        // }
                        onClick={(e) => setRoleAnchor(e.target)}
                        className="h-9 gap-[6px] rounded border !border-[hsl(214,32%,91%)] px-2 py-[6px] text-sm font-medium text-[#395BA9] w-[max-content]"
                    >
                        Role: {capitalizeFirstLetter(roleFilter)}
                    </button>

                    <Menu
                        className="shadow-[none]"
                        open={Boolean(roleAnchor)}
                        anchorEl={roleAnchor}
                        onClose={() => setRoleAnchor(null)}
                    >
                        <MenuItem
                            className="!text-sm text-[#64748b] border-b border-[#F1F5F9] capitalize"
                            onClick={() => setRoleFilter('all')}
                        >
                            All
                        </MenuItem>
                        {roles?.map((role) => {
                            return (
                                <MenuItem
                                    className="!text-sm text-[#64748b] border-b border-[#F1F5F9] capitalize"
                                    onClick={() => setRoleFilter(role)}
                                >
                                    {role}
                                </MenuItem>
                            );
                        })}
                    </Menu>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        all_users: state?.merchantReducers?.all_merchant_users,
        pricing_plan: state?.authReducers?.pricing_plan,
        userguide_info: state?.guideReducers?.userguide_info?.[0]?.users,
        all_article_header: state?.articlesReducers?.all_article_header,
        role: state?.generalReducers?.user_info?.role,
    };
};
export default connect(mapStateToProps, { GetAllMerchantUsers })(Users);
